import { inject, observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { ModuleContainer, ModuleDescription, ModuleIndex, ModuleTitle } from '../parts';
import PathIcon from '../PathIcon';
import ProgressEnum from '../../../../../models/learning/ProgressEnum';
import { PrimaryButton, SecondaryButton, SuccessPill } from '../projectButtons';
import ActionButton from '../Study/ActionButton';

const PreAssessment = ({ learningId, pathId, icon, module, disabled, learningStore }) => {
  const [assignment, setAssignment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);
  const active = module.progress === ProgressEnum.InProgress || module.progress === ProgressEnum.Completed;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Read first. Assignment now happens at enrollment, and a survey-gated card should not be
        // the thing that mints rows. Enrollments predating that get the lazy create as a fallback.
        let a = await learningStore.fetchProjectAssignmentStatus(learningId);
        if (!a) a = await learningStore.fetchProjectAssignment(learningId, pathId, module.id);
        if (!cancelled) setAssignment(a);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    }
    load();

    // The survey submits in its own tab and redirects that tab, leaving this one stale. Re-read on
    // focus so coming back here unlocks the download without a manual refresh.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [learningId, pathId, module.id, learningStore]);

  const onDownload = async () => {
    setErr(null);
    setBusy(true);
    try {
      await learningStore.downloadAssignedProject(learningId, pathId, module.id);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onSubmitClick = () => fileRef.current && fileRef.current.click();

  const onFile = async e => {
    // Capture the input node now: React 16 pools synthetic events, so by the time the awaits below
    // settle, `e.target` has been recycled to null and clearing it would throw. Failing to clear it
    // also means re-picking the SAME file fires no change event, so a retry silently does nothing.
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      await learningStore.uploadPreSubmission(learningId, pathId, module.id, file);
      const ul = await learningStore.getUserLearning(learningId);
      if (ul) setAssignment(await learningStore.fetchProjectAssignmentStatus(learningId));
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
      input.value = '';
    }
  };

  const completed = module.progress === ProgressEnum.Completed;
  // Absent flag never unlocks: the server is the real gate, this only mirrors it.
  const preSurveyCompleted = !!(assignment && assignment.preSurveyCompleted);
  const locked = !!assignment && !preSurveyCompleted;

  return (
    <ModuleContainer className="p3">
      <div className="flex justify-between">
        <ModuleIndex className={`${active ? 'active' : ''}`}>
          {(module.index + 1).toString().padStart(2, '0')}
        </ModuleIndex>
        <PathIcon icon={icon} active={active} />
      </div>
      <div>
        <ModuleTitle>{module.name || 'Pre-Assessment'}</ModuleTitle>
        <ModuleDescription>{module.description}</ModuleDescription>
      </div>
      {assignment ? (
        <p className="my2" style={{ fontSize: 14, color: '#5c6573' }}>
          Assigned project: <strong>project{assignment.assignedProjectIndex}.zip</strong>
        </p>
      ) : (
        <p className="my2" style={{ fontSize: 14, color: '#969dab' }}>
          {err ? `Error: ${err}` : 'Assigning your project…'}
        </p>
      )}
      {locked && (
        <p className="my2" style={{ fontSize: 13, color: '#b8860b' }}>
          <span role="img" aria-label="locked">
            🔒
          </span>{' '}
          Complete the Pre-Workshop Survey (previous card) to unlock your project download.
        </p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: 'none' }}
        onChange={onFile}
      />
      <div className="flex" style={{ gap: 12, marginTop: 8 }}>
        <div style={{ flex: 1 }}>
          <SecondaryButton
            type="button"
            onClick={onDownload}
            disabled={disabled || !assignment || !preSurveyCompleted || busy}
          >
            <span role="img" aria-label="download">
              ⬇
            </span>
            Download Project
          </SecondaryButton>
        </div>
        <div style={{ flex: 1 }}>
          {completed ? (
            <SuccessPill>
              <span role="img" aria-label="done">
                ✓
              </span>
              Submitted
            </SuccessPill>
          ) : (
            <PrimaryButton type="button" onClick={onSubmitClick} disabled={disabled || !assignment || busy}>
              <span role="img" aria-label="upload">
                ⬆
              </span>
              {busy ? 'Uploading…' : 'Submit Your Project'}
            </PrimaryButton>
          )}
        </div>
      </div>
      {module.workspaceStudyId && (
        <div className="custom-actions" style={{ marginTop: 12 }}>
          <ActionButton
            studyId={module.workspaceStudyId}
            enableWorkspace
            workspace={module.workspace || {}}
            disabled={disabled || locked || module.workspaceLocked}
          />
        </div>
      )}
      {err && (
        <p className="my2" style={{ fontSize: 12, color: '#c0392b' }}>
          {err}
        </p>
      )}
    </ModuleContainer>
  );
};

export default inject('learningStore')(observer(PreAssessment));
